import { modalVariants } from '../../styles';
import { AnimatePresence, motion } from 'framer-motion';
import styled from 'styled-components';
import { useCallback, useEffect, useRef, useState } from 'react';

export const DropdownStyled = styled(motion.div)<{
  $toLeft?: boolean;
  $toTop?: boolean;
  $fullWidth?: boolean;
  $maxHeight?: number | string;
  $padding?: number | string;
}>`
  padding: 12px 12px 12px 12px;
  box-shadow: 0px 15px 30px rgba(0, 0, 0, 0.1);
  border-radius: 25px;
  background: ${({ theme }) => theme.white};
  border: 1px solid #f0f0f0;
  position: absolute;
  ${({ $toLeft }) => ($toLeft ? 'right: 0px;' : 'left: 0px;')}
  transform-origin: ${({ $toLeft }) => ($toLeft ? 'top right' : 'top left')};
  top: 48px;
  overflow: hidden;
  z-index: 10;
  -webkit-overflow-scrolling: touch;

  ${({ $toTop }) =>
    $toTop &&
    `
    top: auto;
    bottom: 50px;


  `}

  @media (min-width: 3500px) {
    width: 700px;
    top: 120px;
  }

  max-height: calc(100vh - 100px);
  ${({ $maxHeight }) => `max-height: ${$maxHeight}`};
  ${({ $padding }) => `padding: ${$padding}`};
  ${({ $fullWidth }) => $fullWidth && `right: 0;`};

  overflow: auto;
  ::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

/**
 * The closest ancestor that scrolls its overflow, i.e. the box that would cut
 * the menu off. `null` when nothing before the document does, in which case
 * the viewport is the only bound.
 */
const findScrollingAncestor = (element: HTMLElement): HTMLElement | null => {
  let node = element.parentElement;

  while (node && node !== document.body) {
    const { overflowY } = getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll') return node;
    node = node.parentElement;
  }

  return null;
};

export const DropdownMenuComponent = ({
  children,
  isOpen,
  maxHeight,
  padding,
  fullWidth,
  toLeft,
  toTop: propToTop,
}: {
  children: React.ReactNode;
  isOpen: boolean;
  maxHeight?: string;
  padding?: string;
  fullWidth?: boolean;
  toLeft?: boolean;
  toTop?: boolean;
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [autoToTop, setAutoToTop] = useState(false);

  const calculatePosition = useCallback(() => {
    const dropdownElement = dropdownRef.current;
    if (!dropdownElement) return;

    // Get the parent element (the element with position: relative/absolute/fixed)
    const parentElement = dropdownElement.offsetParent as HTMLElement;
    if (!parentElement) return;

    const parentRect = parentElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    /*
     * What actually shows the menu is the nearest scrolling ancestor, not the
     * window: inside a modal body the room below the field runs out at the
     * modal's edge while the page still has plenty, and measuring against the
     * window alone left the menu opening down and cut off there.
     */
    const clipper = findScrollingAncestor(parentElement);
    const clipperRect = clipper?.getBoundingClientRect();

    const limitTop = Math.max(0, clipperRect?.top ?? 0);
    const limitBottom = Math.min(
      viewportHeight,
      clipperRect?.bottom ?? viewportHeight
    );

    // Get the actual dropdown height after it's rendered
    const dropdownHeight =
      dropdownElement.offsetHeight || dropdownElement.scrollHeight || 200;

    // Space either side of the field, up to whatever bounds the menu.
    // Opening down starts 48px below the top of the parent (as per CSS).
    const spaceAbove = parentRect.top - limitTop;
    const spaceBelow = limitBottom - parentRect.bottom;

    const wouldOverflowBottom = spaceBelow < dropdownHeight + 48;

    // Only flip to top if:
    // 1. It would overflow the bottom AND
    // 2. There's more space above than below (or it would definitely overflow)
    if (
      wouldOverflowBottom &&
      (spaceAbove > spaceBelow || spaceAbove >= dropdownHeight + 60)
    ) {
      setAutoToTop(true);
    } else {
      setAutoToTop(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      // Use requestAnimationFrame to ensure the dropdown is fully rendered
      // Then calculate position after a brief delay to get accurate measurements
      let timeoutId: NodeJS.Timeout;
      const rafId = requestAnimationFrame(() => {
        timeoutId = setTimeout(() => {
          calculatePosition();
        }, 10);
      });

      return () => {
        cancelAnimationFrame(rafId);
        if (timeoutId) clearTimeout(timeoutId);
      };
    }
  }, [isOpen, children, calculatePosition]);

  useEffect(() => {
    if (!isOpen) return;

    const handleResize = () => {
      calculatePosition();
    };

    const handleScroll = () => {
      calculatePosition();
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen, calculatePosition]);

  // Use prop value if provided, otherwise use auto-calculated value
  const toTop = propToTop !== undefined ? propToTop : autoToTop;

  return (
    <AnimatePresence>
      {isOpen && (
        <DropdownStyled
          ref={dropdownRef}
          key="DropdownMenu"
          variants={modalVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          $maxHeight={maxHeight}
          $padding={padding}
          $fullWidth={fullWidth}
          $toLeft={toLeft}
          $toTop={toTop}
        >
          {children}
        </DropdownStyled>
      )}
    </AnimatePresence>
  );
};
